import {useState} from 'react';
import {useTheme} from '@mui/material';

import IconNamesType from 'presentation/icons/IconNamesType';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';

import {RecurrenceTypeEnum} from 'domain/usecases';
import {ODONTO_AUTO_PAYMENT_URL} from '../../constants';
import type {UseAutoPaymentCardProps, UseAutoPaymentCardReturn} from './types';

const {disabled} = translations['pt-br'].privateFlow.myFinancial.openBills;
const {debitAccount, creditCard} =
	translations['pt-br'].privateFlow.myFinancial.openBills.autoPaymentCard;

export const useAutoPaymentCard = ({
	type,
	onEnable,
	onChange,
	onDeactivate,
	isEnableLoading,
	isChangeLoading,
	isDeactivateLoading,
}: UseAutoPaymentCardProps): UseAutoPaymentCardReturn => {
	const {palette} = useTheme();
	const {isMobile} = useMediaQueries();
	const {odontoOnly} = useAccountInformation();
	const [debitWarningOpen, setDebitWarningOpen] = useState(false);
	const [errorTooltipOpen, setErrorTooltipOpen] = useState(false);
	const [lastClickedAction, setLastClickedAction] = useState<
		'enable' | 'change' | 'deactivate' | null
	>(null);

	const knownActiveTypes = [
		RecurrenceTypeEnum.CREDIT,
		RecurrenceTypeEnum.CREDIT_CIELO,
		RecurrenceTypeEnum.CREDIT_BRASPAG,
		RecurrenceTypeEnum.DEBIT,
	];
	const isDisabled = !type || !knownActiveTypes.includes(type);
	const isCreditCard =
		type === RecurrenceTypeEnum.CREDIT ||
		type === RecurrenceTypeEnum.CREDIT_CIELO ||
		type === RecurrenceTypeEnum.CREDIT_BRASPAG;
	const isDebitAccount = type === RecurrenceTypeEnum.DEBIT;
	const isOdontoRedirect = odontoOnly && type !== RecurrenceTypeEnum.DEBIT;

	const getLabel = () => {
		if (isDisabled) return disabled;
		if (isCreditCard) return creditCard;
		return debitAccount;
	};

	const label = getLabel();
	const iconName = (
		isDisabled ? 'check-circle-outline-filled' : 'check-circle-filled'
	) as IconNamesType;
	const iconColor = isDisabled ? palette.text.secondary : palette.success.main;

	const snackPosition = isMobile
		? {vertical: 'top' as const, horizontal: 'center' as const}
		: {vertical: 'top' as const, horizontal: 'right' as const};

	const resolvedEnableLoading = lastClickedAction === 'enable' && (isEnableLoading ?? false);
	const resolvedChangeLoading =
		!isDebitAccount && lastClickedAction === 'change' && (isChangeLoading ?? false);
	const resolvedDeactivateLoading =
		lastClickedAction === 'deactivate' && (isDeactivateLoading ?? false);

	const openOdontoRedirectUrl = () => {
		window.open(ODONTO_AUTO_PAYMENT_URL, '_blank');
	};

	const handleChange = () => {
		if (isOdontoRedirect) {
			openOdontoRedirectUrl();
			return;
		}
		if (isDebitAccount) {
			setDebitWarningOpen(true);
			return;
		}
		setLastClickedAction('change');
		setErrorTooltipOpen(false);
		onChange?.();
	};

	const handleEnable = () => {
		if (isOdontoRedirect) {
			openOdontoRedirectUrl();
			return;
		}
		setLastClickedAction('enable');
		setErrorTooltipOpen(false);
		onEnable?.();
	};

	const handleDeactivate = () => {
		if (isOdontoRedirect) {
			openOdontoRedirectUrl();
			return;
		}
		setLastClickedAction('deactivate');
		setErrorTooltipOpen(false);
		onDeactivate?.();
	};

	return {
		isDisabled,
		isCreditCard,
		isDebitAccount,
		isOdontoRedirect,
		isMobile,
		label,
		iconName,
		iconColor,
		snackPosition,
		debitWarningOpen,
		errorTooltipOpen,
		resolvedEnableLoading,
		resolvedChangeLoading,
		resolvedDeactivateLoading,
		handleChange,
		handleEnable,
		handleDeactivate,
		setDebitWarningOpen,
		setErrorTooltipOpen,
	};
};
